import { assetsListBySymbol } from 'abis/set/asset-icons'
import AboutIcon from 'assets/menu-icons/nested-menu/about.svg?react'
import DocIcon from 'assets/menu-icons/nested-menu/documentation.svg?react'
import InvValueIcon from 'assets/menu-icons/nested-menu/investment-value.svg?react'
import PortfolioIcon from 'assets/menu-icons/nested-menu/portfolio.svg?react'
import ReturnIcon from 'assets/menu-icons/nested-menu/return.svg?react'
import StakingIcon from 'assets/menu-icons/nested-menu/staking.svg?react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Path from 'router'

/**
 * Navigation constants
 */
const PRODUCTS = 'products'
const PORTFOLIO = 'portfolio'
const COMPANY = 'company'

/**
 * Navigation List
 */
const navigationList = [PRODUCTS, PORTFOLIO, COMPANY]

export interface NestedNavListProps {
  title: string
  isPortfolio?: boolean
  data: {
    icon: string | React.FunctionComponent<React.SVGProps<SVGSVGElement>>
    name: string
    description: string
    link?: string
    colored?: boolean
  }[]
}

/**
 * Nested Navigation List
 */
const nestedNavigationList = (invValue?: string, returnValue?: string): NestedNavListProps[] => [
  {
    title: 'Products',
    data: [
      {
        icon: assetsListBySymbol['CAI'].icon,
        name: 'Colony Avalanche Index',
        description: 'The benchmark index for Avalanche',
        link: `${Path.PRODUCTS}/cai`,
      },
      {
        icon: assetsListBySymbol['JMX'].icon,
        name: 'JOOCE Memecoin Index',
        description: 'The ultimate memecoin index',
        link: `${Path.PRODUCTS}/jmx`,
      },
      {
        icon: assetsListBySymbol['ARTXAI'].icon,
        name: 'ARTX AI Smart Fund',
        description: 'AI powered fund exposed to megacaps',
        link: `${Path.PRODUCTS}/artxai`,
      },
    ],
  },
  {
    title: 'Portfolio',
    isPortfolio: true,
    data: [
      {
        icon: PortfolioIcon,
        name: 'View portfolio',
        description: 'for a further breakdown',
        link: `${Path.PORTFOLIO}`,
      },
      {
        icon: StakingIcon,
        name: 'View staking',
        description: 'for information on your PHTR',
        link: `${Path.STAKING}`,
      },
      {
        icon: InvValueIcon,
        name: `${invValue || '0'}`,
        description: 'Investment value',
      },
      {
        icon: ReturnIcon,
        name: `${returnValue || '0'}`,
        description: 'Return',
        colored: true,
      },
    ],
  },
  {
    title: 'Company',
    data: [
      {
        icon: AboutIcon,
        name: 'About',
        description: 'Learn more about our team and mission',
        link: `${Path.ABOUT}`,
      },
      {
        icon: DocIcon,
        name: 'Documentation',
        description: 'Technical details of how Phuture works',
        link: 'https://docs.phuture.finance/',
      },
    ],
  },
]

/**
 * Extracts from a the pathname from url
 */
const extractPathName = (pathname: string) => {
  const splitUrl = pathname.split('/')
  if (splitUrl.length > 0) {
    return splitUrl[1]
  }
  return ''
}

export interface ActiveLinking {
  nestedNavigationList: (invValue?: string, returnValue?: string) => NestedNavListProps[]
  navigationList: string[]
  activePath: string
  isElementActive: (path: string) => boolean
}
/**
 * A useEffect that listens to the pathname and sets the active path across desktop/mobile
 * @returns
 */
export const useActiveLinking = (): ActiveLinking => {
  const { pathname } = useLocation()
  const [activePath, setActivePath] = useState<string>(extractPathName(pathname))
  useEffect(() => {
    // if the pathname is not the on the navigation list. then unset active path
    if (
      navigationList.includes(extractPathName(pathname)) ||
      navigationList.map((el) => el.replace(' ', '-')).includes(extractPathName(pathname))
    ) {
      setActivePath(extractPathName(pathname))
    } else {
      setActivePath('')
    }
  }, [pathname, setActivePath])

  const isElementActive = (path: string) => {
    return path === activePath
  }

  return {
    navigationList,
    nestedNavigationList,
    activePath,
    isElementActive,
  }
}
